import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Trash2, Search, UserPlus, AlertTriangle } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Producto } from '@/api/tienda.api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';
import { dinero, porcentaje } from '@/lib/formato-tienda';

/**
 * NUEVA VENTA — una factura, no un modal de dos campos.
 *
 * ═══ EL AVISO DE LA TALLA QUE VUELVE ═══
 *
 * Al elegir una talla, si esa talla tiene una tasa de devolución alta se dice
 * ahí mismo. Es el dato que ya existe en «Qué tallas te vuelven», puesto donde
 * sirve: en el momento de vender, no en un reporte que se lee después.
 *
 * El tallaje chino corre pequeño y casi todas las devoluciones de esta tienda
 * son por eso. Avisarlo antes de cerrar la venta ahorra el viaje del mensajero.
 */

interface Linea {
  productVariantId: string;
  /** Nombre del producto. Se trunca si no cabe. */
  etiqueta: string;
  /**
   * La talla, aparte y SIN truncar.
   *
   * Iba pegada al nombre en una sola cadena y el `truncate` se la comía: dos
   * líneas del mismo vestido en M y en S se veían idénticas —«Vestido rosado
   * de cu…»— justo en la pantalla donde se arma lo que va a salir en la funda.
   */
  talla: string;
  disponible: number;
  cantidad: number;
  unitPrice: number;
  descuento: number;
  /** Tasa de devolución de esta talla, si la hay. */
  tasaDevolucion?: number | null;
}

export function NuevaVenta({
  onCerrar,
  onCreada,
}: {
  onCerrar: () => void;
  onCreada: () => void;
}) {
  const toast = useToast();
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [buscar, setBuscar] = useState('');
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [buscarCliente, setBuscarCliente] = useState('');
  const [clienteNuevo, setClienteNuevo] = useState({ nombre: '', telefono: '', direccion: '' });
  const [creandoCliente, setCreandoCliente] = useState(false);
  const [formaDePago, setFormaDePago] = useState('contra entrega');
  const [envio, setEnvio] = useState('');
  const [descuento, setDescuento] = useState('');

  const { data: productos } = useQuery({
    queryKey: ['productos', buscar],
    queryFn: () => tienda.listarProductos({ buscar: buscar || undefined, limit: 30 }),
    enabled: buscar.length > 0,
  });

  const { data: clientes } = useQuery({
    queryKey: ['clientes', buscarCliente],
    queryFn: () => tienda.listarClientes({ buscar: buscarCliente || undefined, limit: 8 }),
  });

  const { data: devoluciones } = useQuery({
    queryKey: ['tienda', 'devoluciones', 'mes'],
    queryFn: () => tienda.devolucionesPorTalla('mes'),
  });

  const tasaDe = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const d of devoluciones ?? []) {
      if (d.tasa != null) mapa.set(`${d.sku ?? ''}|${d.talla}`, d.tasa);
    }
    return mapa;
  }, [devoluciones]);

  const crearCliente = useMutation({
    mutationFn: () => tienda.crearCliente(clienteNuevo),
    onSuccess: (c) => {
      setClienteId(c.id);
      setCreandoCliente(false);
      toast.success(`Cliente ${c.nombre} guardado`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  const crear = useMutation({
    mutationFn: () =>
      tienda.crearVenta({
        lineas: lineas.map((l) => ({
          productVariantId: l.productVariantId,
          cantidad: l.cantidad,
          unitPrice: l.unitPrice,
          descuento: l.descuento || undefined,
        })),
        clienteId,
        formaDePago,
        envio: envio ? Number(envio) : undefined,
        descuento: descuento ? Number(descuento) : undefined,
      }),
    onSuccess: () => {
      toast.success('Venta creada en borrador. Confírmala para que descuente.');
      onCreada();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear'),
  });

  function agregar(p: Producto, varianteId: string) {
    const v = p.variants.find((x) => x.id === varianteId);
    if (!v || lineas.some((l) => l.productVariantId === varianteId)) return;
    setLineas((prev) => [
      ...prev,
      {
        productVariantId: varianteId,
        etiqueta: p.name,
        talla: v.label,
        disponible: v.stock,
        cantidad: 1,
        unitPrice: v.price ?? p.price,
        descuento: 0,
        tasaDevolucion: tasaDe.get(`${p.sku ?? ''}|${v.label}`) ?? null,
      },
    ]);
  }

  const bruto = lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0);
  const total = Math.max(0, bruto - (Number(descuento) || 0)) + (Number(envio) || 0);
  const algunaSinExistencia = lineas.some((l) => l.cantidad > l.disponible);

  return (
    <Modal open onClose={onCerrar} title="Nueva venta" size="lg">
      <div className="space-y-4">
        {/* ── Cliente ─────────────────────────────────────────────────────── */}
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Cliente
          </label>
          {clienteId ? (
            <div className="flex items-center justify-between rounded-lg border border-surface-border px-3 py-2 text-sm">
              <span>{clientes?.find((c) => c.id === clienteId)?.nombre ?? 'Seleccionada'}</span>
              <button
                type="button"
                onClick={() => setClienteId(null)}
                className="text-xs text-slate-500 underline"
              >
                cambiar
              </button>
            </div>
          ) : creandoCliente ? (
            <div className="space-y-2 rounded-lg border border-surface-border p-3">
              <Input
                placeholder="Nombre"
                value={clienteNuevo.nombre}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, nombre: e.target.value })}
              />
              <Input
                placeholder="Teléfono"
                value={clienteNuevo.telefono}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, telefono: e.target.value })}
              />
              <Input
                placeholder="Dirección de entrega"
                value={clienteNuevo.direccion}
                onChange={(e) => setClienteNuevo({ ...clienteNuevo, direccion: e.target.value })}
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setCreandoCliente(false)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={() => crearCliente.mutate()}
                  disabled={!clienteNuevo.nombre.trim() || crearCliente.isPending}
                >
                  Guardar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Busca por nombre o teléfono…"
                  value={buscarCliente}
                  onChange={(e) => setBuscarCliente(e.target.value)}
                />
              </div>
              {buscarCliente && (clientes ?? []).length > 0 && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-surface-border">
                  {clientes!.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setClienteId(c.id)}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <span>{c.nombre}</span>
                      <span className="text-xs text-slate-400">{c.telefono ?? ''}</span>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setCreandoCliente(true)}
                className="mt-1 inline-flex items-center gap-1 text-xs text-brand-700 underline dark:text-brand-300"
              >
                <UserPlus className="h-3 w-3" />
                Es nueva, guárdala
              </button>
            </>
          )}
        </div>

        {/* ── Piezas ──────────────────────────────────────────────────────── */}
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Qué se lleva
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-8"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder="Busca por código o por nombre…"
            />
          </div>
        </div>

        {buscar && productos && productos.length > 0 && (
          <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border border-surface-border p-2">
            {productos.map((p) => (
              <div key={p.id}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  <span className="font-mono text-slate-500">{p.sku}</span> {p.name}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {p.variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      disabled={v.stock <= 0}
                      onClick={() => agregar(p, v.id)}
                      className={
                        v.stock <= 0
                          ? 'cursor-not-allowed rounded-md border border-surface-border bg-slate-50 px-2 py-1 text-xs text-slate-400 dark:bg-slate-800'
                          : 'rounded-md border border-surface-border px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-800'
                      }
                    >
                      {v.label} {v.stock <= 0 ? '(agotada)' : `(${v.stock})`}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {lineas.length === 0 ? (
          <p className="py-3 text-center text-sm text-slate-400">
            Busca una pieza arriba y elige la talla.
          </p>
        ) : (
          <div className="space-y-2">
            {lineas.map((l, i) => (
              <div key={l.productVariantId} className="space-y-1">
                <div className="flex items-center gap-2">
                  {/* `min-w-0` + `flex-1` no bastan: el `Input` se envuelve en
                      un `div.w-full` propio, y dos de esos se comen la fila
                      entera dejando el nombre del producto en cero píxeles.
                      Se acota cada uno desde fuera con `shrink-0`. */}
                  <span
                    className="flex min-w-0 flex-1 items-baseline gap-1 text-sm"
                    title={`${l.etiqueta} · ${l.talla}`}
                  >
                    <span className="min-w-0 truncate">{l.etiqueta}</span>
                    <span className="shrink-0 font-medium text-slate-500">· {l.talla}</span>
                  </span>
                  <div className="w-16 shrink-0">
                  <Input
                    type="number"
                    value={String(l.cantidad)}
                    onChange={(e) =>
                      setLineas((p) =>
                        p.map((x, j) =>
                          j === i ? { ...x, cantidad: Math.max(1, Number(e.target.value) || 1) } : x,
                        ),
                      )
                    }
                  />
                  </div>
                  <div className="w-20 shrink-0">
                  <Input
                    type="number"
                    title="Rebaja de esta pieza"
                    placeholder="rebaja"
                    value={l.descuento ? String(l.descuento) : ''}
                    onChange={(e) =>
                      setLineas((p) =>
                        p.map((x, j) =>
                          j === i ? { ...x, descuento: Math.max(0, Number(e.target.value) || 0) } : x,
                        ),
                      )
                    }
                  />
                  </div>
                  <span className="w-24 shrink-0 text-right text-sm tabular-nums">
                    {dinero(l.unitPrice * l.cantidad - l.descuento)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setLineas((p) => p.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {(l.tasaDevolucion ?? 0) >= 0.3 && (
                  <p className="pl-1 text-xs text-amber-700 dark:text-amber-400">
                    Ojo: de esta talla vuelve el {porcentaje(l.tasaDevolucion!)}. Avísale del
                    tallaje antes de cerrar.
                  </p>
                )}
              </div>
            ))}

            {algunaSinExistencia && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                Hay una línea que pide más de lo que hay. Al confirmar se va a rechazar entera: no
                se descuenta media venta.
              </p>
            )}
          </div>
        )}

        {/* ── Cierre ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Forma de pago</label>
            <Select value={formaDePago} onChange={(e) => setFormaDePago(e.target.value)}>
              <option value="contra entrega">Contra entrega</option>
              <option value="transferencia">Transferencia</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Envío</label>
            <Input
              type="number"
              value={envio}
              onChange={(e) => setEnvio(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Descuento</label>
            <Input
              type="number"
              value={descuento}
              onChange={(e) => setDescuento(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>

        {formaDePago === 'contra entrega' && lineas.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-900/20 dark:text-sky-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {/* El texto va dentro de UN hijo. Suelto en el flex, cada trozo
                —incluido el <strong>— se volvía su propia columna y la frase
                salía partida en tres. */}
            <p>
              Contra entrega: al confirmar baja la existencia, pero la venta queda{' '}
              <strong>por cobrar</strong> hasta que vuelva el mensajero.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-surface-border pt-2">
          <span className="text-sm font-medium">Total</span>
          <span className="text-lg font-semibold tabular-nums">{dinero(total)}</span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={() => crear.mutate()} disabled={lineas.length === 0 || crear.isPending}>
            {crear.isPending ? 'Creando…' : 'Crear borrador'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
